import SelectField, {
	ISelectFieldOption,
} from '@/components/fields/SelectField';
import { confirmModal, modal } from '@/components/modal/Modal';
import { useKeycloak } from '@/contexts/KeycloakContext';
import { Button } from '@/styles/buttons';
import { Row } from '@/styles/common';
import { ScenarioHeader } from '@/styles/scenario';
import axios from '@/utils/axios';
import { buildParams, formatDate } from '@/utils/utils';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';

interface CommonScenarioHeaderProps {
	scenarioId: TValue | undefined;
	setScenarioId: (value: TValue | undefined) => void;
	scenarios: ISelectFieldOption[] | undefined;
	type: TScenarioType;
}

const CommonScenarioHeader: React.FC<CommonScenarioHeaderProps> = ({
	scenarioId,
	scenarios,
	setScenarioId,
	type,
}) => {
	const { keycloak } = useKeycloak();
	const queryClient = useQueryClient();

	const selectedScenario = scenarios?.find(
		(option) => option.value === scenarioId
	);

	const { mutate: deleteScenario } = useMutation(
		[`delete_${type}_scenario`],
		() =>
			axios.delete(`/${type}_scenario`, {
				data: {
					id: scenarioId,
					name: selectedScenario!.label,
					last_modified_by: keycloak?.tokenParsed?.preferred_username,
					last_modified_datetime: new Date(),
				},
			}),
		{
			onSuccess: () => {
				queryClient.invalidateQueries([`${type}_scenarios`]);
				setScenarioId(undefined);
			},
		}
	);

	const { mutate: createScenario } = useMutation(
		[`add_${type}_scenario`],
		(name: string) =>
			axios.post<TScenario>(`/${type}_scenario`, {
				name,
				last_modified_by: keycloak?.tokenParsed?.preferred_username,
				last_modified_datetime: new Date(),
			}),
		{
			onSuccess: (data) => {
				queryClient.invalidateQueries([`${type}_scenarios`]);
				setScenarioId(data.data.id);
			},
		}
	);

	const { mutate: copyScenario } = useMutation(
		[`add_${type}_scenario`],
		({ name, scenario_from_id }: { name: string; scenario_from_id: number }) =>
			axios.post<TScenario>(
				`/${type}_scenario/copyscenario${buildParams({
					[`${type}_scenario_id`]: scenario_from_id,
				})}`,
				{
					name,
					last_modified_by: keycloak?.tokenParsed?.preferred_username,
					last_modified_datetime: new Date(),
				}
			),
		{
			onSuccess: (data) => {
				queryClient.invalidateQueries([`${type}_scenarios`]);
				setScenarioId(data.data.id);
			},
		}
	);

	return (
		<ScenarioHeader>
			<div>
				<Row className='full'>
					<SelectField
						options={scenarios ?? []}
						title='Load a scenario'
						value={scenarioId}
						setValue={setScenarioId}
						variant='secondary'
					/>
					<Button
						variant='secondary'
						onClick={() => {
							modal.editFields({
								data: {
									name: '',
									scenario: -1,
								},
								description: {
									scenario: {
										type: 'list',
										list: scenarios,
										label: 'Scenario',
										title: 'Choose a scenario',
										variant: 'base',
									},
									name: {
										type: 'string',
										label: 'Name',
									},
								},
								title: 'Add a scenario',
								setRow: async (data, setRowField) => {
									if (data.name.trim() === '') {
										modal.error({
											message: 'The name cannot be empty',
										});
										setRowField?.('name', '');
										return false;
									}
									if (data.scenario !== -1) {
										copyScenario({
											name: data.name,
											scenario_from_id: data.scenario,
										});
										return true;
									}
									createScenario(data.name);
									return true;
								},
								setData: ({ data, fieldName, value }) => {
									data[fieldName] = value;
								},
							});
						}}>
						Add a scenario
					</Button>
				</Row>
			</div>
			{selectedScenario && <h2>Scenario {selectedScenario.label}</h2>}

			<div>
				{selectedScenario && (
					<Row center className='full'>
						<Button
							variant='secondary'
							onClick={async () => {
								try {
									const res = await axios.get(
										`/${type}_scenario_xlsx?scenario_id=${scenarioId}`,
										{
											responseType: 'blob',
										}
									);
									const fileName = `${selectedScenario.label}-${formatDate({
										date: new Date(),
										dateOnly: false,
									})}.xlsx`;
									const blob = new Blob([res.data], {
										type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
									});
									const href = URL.createObjectURL(blob);
									const link = document.createElement('a');
									link.setAttribute('href', href);
									link.setAttribute('download', fileName);
									link.click();

									URL.revokeObjectURL(href);
								} catch (e) {
									modal.error({
										message: 'Error downloading the file',
									});
								}
							}}>
							Export this scenario
						</Button>
						<Button
							variant='secondary'
							onClick={async () => {
								if (await confirmModal({ title: 'Confirmation' })) {
									deleteScenario();
								}
							}}>
							Delete this scenario
						</Button>
					</Row>
				)}
			</div>
		</ScenarioHeader>
	);
};

export default CommonScenarioHeader;
